import { DecimalPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  AfterViewInit,
  Optional,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormGroupDirective, UntypedFormControl } from '@angular/forms';
import { dirtyOrTouchedErrorStateMatcher } from '../../constants/dirty-or-touched-error-state-matcher';
import { isDigitOnlyKeyAllowed } from '../../functions/is-digit-only-key-allowed';
import { parsePastedInteger } from '../../functions/parse-pasted-integer';
import { validationMessages } from '../../messages';

@Component({
  selector: 'app-ui-input',
  templateUrl: './ui-text-input.component.html',
  styleUrls: ['./ui-text-input.component.scss'],
  providers: [DecimalPipe],
  standalone: false,
  host: {
    '[class.ui-form-field--no-hint-row]': 'showClearButton',
  },
})
export class UiTextInputComponent implements AfterViewInit {
  @ViewChild('input') input: ElementRef;
  @Input() label = '';
  @Input() description = '';
  @Input() control: UntypedFormControl;
  @Input() placeholder: string;
  @Input() messages = validationMessages;
  @Input() required = false;
  @Input() type: 'text' | 'number' | 'file' = 'text';
  @Input() formatNumber = false;
  @Input() tabIndex: number;
  @Input() prependPlusSingForPositives = false;
  @Input() showClearButton = false;
  @Input() prefixIcon = '';
  @Input() ariaLabel = '';
  @Input() digitsOnly = false;
  @Input() min: number | string | null = null;
  @Input() max: number | string | null = null;

  @Output() readonly changeHandler: EventEmitter<string> = new EventEmitter<string>();

  readonly errorStateMatcher = dirtyOrTouchedErrorStateMatcher;

  private invalidChars = ['E', 'e', ','];

  constructor(
    private decimalPipe: DecimalPipe,
    @Optional() private readonly parentForm: FormGroupDirective
  ) {}

  get hasValue(): boolean {
    return (
      this.control?.value !== null &&
      this.control?.value !== undefined &&
      this.control?.value !== ''
    );
  }

  get showErrorIcon(): boolean {
    return this.errorStateMatcher.isErrorState(this.control, this.parentForm);
  }

  ngAfterViewInit(): void {
    if (this.control) {
      this.formatInput();
    }
  }

  keyDown(event: KeyboardEvent): void {
    if (this.digitsOnly && !isDigitOnlyKeyAllowed(event)) {
      event.preventDefault();
      return;
    }

    if (this.type === 'number' && this.invalidChars.includes(event.key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (!this.digitsOnly || this.control?.disabled) {
      return;
    }

    event.preventDefault();
    const value = parsePastedInteger(event.clipboardData?.getData('text') ?? '');
    if (value === null) {
      return;
    }

    this.control.setValue(value, { emitEvent: true });
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  clearInput(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.hasValue || this.control.disabled) {
      return;
    }

    this.input.nativeElement.value = '';
    this.control.setValue(this.type === 'number' ? null : '');
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.changeHandler.emit('');
    this.input.nativeElement.focus();
  }

  formatInput(): void {
    this.changeHandler.emit(this.input.nativeElement.value);
    if (!this.formatNumber) {
      return;
    }

    const value = this.input.nativeElement.value.replace(/\D/g, ''); // remove non-numeric characters
    const formattedValue = this.decimalPipe.transform(value, '1.0-0'); // format value with 0 decimal places
    this.input.nativeElement.value = formattedValue || ''; // set input value to formatted value or empty string if undefined/null
    this.control.setValue(value, { emitModelToViewChange: false }); // update FormControl's value with unformatted value
  }
}
